import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem as MuiMenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { PlusIcon, TrashIcon, ClipboardDocumentIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import { AppIconButton } from '../ui';
import { inventoryApi } from '../../api/inventoryApi';
import { formatCurrency } from '../../config/currency';
import { formatQuantity, formatQuantityParts } from '../../utils/formatQuantity';
import type { ThemeColors } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';

export interface PlannerPortionOption {
  id: number;
  portionName: string;
}

export interface PlannerMenuItemOption {
  id: number;
  name: string;
  categoryName?: string;
  portions?: PlannerPortionOption[];
}

interface PlannerLine {
  menuItemId: number;
  menuItemName: string;
  portionId: number | null;
  portionName: string | null;
  quantity: number;
}

interface IngredientRequirement {
  inventoryItemId: number;
  name: string;
  unit: string;
  requiredQuantity: number;
  currentStock: number;
  shortfall: number;
  unitCost: number | null;
  estimatedCost: number | null;
}

/** Raw ingredient of a prepared (kitchen-made) item — quantity only, no cost. */
interface PrepIngredientShort {
  inventoryItemId: number;
  name: string;
  unit: string;
  requiredQuantity: number;
  currentStock: number;
  shortfall: number;
}

interface PrepShortfall {
  inventoryItemId: number;
  name: string;
  unit: string;
  quantityToMake: number;
  ingredients: PrepIngredientShort[];
}

interface UnmappedLine {
  menuItemId: number;
  menuItemName: string;
  portionId: number | null;
  portionName: string | null;
}

interface EstimateResult {
  ingredients: IngredientRequirement[];
  totalEstimatedCost: number | null;
  unmapped: UnmappedLine[];
  preps: PrepShortfall[];
}

interface SavedPlan {
  id: number;
  name: string;
  updatedAt?: string;
  lines: { menuItemId: number; portionId: number | null; quantity: number }[];
}

interface SalesHistoryLine {
  menuItemId: number;
  menuItemName: string;
  portionId: number | null;
  portionName: string | null;
  quantity: number;
}

/** A single dish's ingredient breakdown for prep-sheet mode. */
interface PrepSheetGroup {
  line: PlannerLine;
  ingredients: IngredientRequirement[];
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const PAYMENT_MODES = ['CASH', 'UPI', 'CARD', 'BANK_TRANSFER'];

/** Compact base-unit label (kg, g, L, ml, pcs) — value 1 never crosses a scaling boundary. */
const unitLabel = (unit: string) => formatQuantityParts(1, unit).label;

const pad2 = (n: number) => String(n).padStart(2, '0');
const toIsoDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDays = (d: Date, n: number) => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};

interface ApiError {
  response?: { data?: { error?: string; message?: string } };
}

interface PlannerTabProps {
  menuItems: PlannerMenuItemOption[];
  theme: ThemeColors;
}

/** Compact ingredient list used by prep-sheet groups and the kitchen prep section. */
const IngredientList: React.FC<{
  ings: (PrepIngredientShort | IngredientRequirement)[];
  theme: ThemeColors;
}> = ({ ings, theme }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    {ings.map((ing) => (
      <Box
        key={ing.inventoryItemId}
        data-testid="planner-mini-ingredient"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.25,
          py: 0.75,
          borderRadius: 1,
          border: `1px solid ${ing.shortfall > 0 ? theme.warning : theme.components?.card?.border}`,
          backgroundColor: ing.shortfall > 0 ? `${theme.warning}10` : theme.background,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>{ing.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.text }}>
            Need {formatQuantity(ing.requiredQuantity, ing.unit)}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
            In stock {formatQuantity(ing.currentStock, ing.unit)}
          </Typography>
          {ing.shortfall > 0 && (
            <Typography variant="caption" sx={{ color: theme.warning, fontWeight: 700 }}>
              Buy {formatQuantity(ing.shortfall, ing.unit)}
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

/**
 * Ingredient planner: build a list of planned dishes ("200 idli, 5 family
 * pack biryani") and estimate the total ingredients needed, compared against
 * current stock. Supports sales-driven auto-fill, saved plans, scaling,
 * per-dish prep sheets, shopping-list sharing and marking purchases.
 */
const PlannerTab: React.FC<PlannerTabProps> = ({ menuItems, theme }) => {
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  const confirm = useConfirm();
  const [lines, setLines] = useState<PlannerLine[]>([]);
  const [newItemId, setNewItemId] = useState<number | ''>('');
  const [newPortionId, setNewPortionId] = useState<number | ''>('');
  const [newQuantity, setNewQuantity] = useState<number>(0);
  const [result, setResult] = useState<EstimateResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fill from sales
  const [fillAnchor, setFillAnchor] = useState<null | HTMLElement>(null);
  const [fillLoading, setFillLoading] = useState(false);
  const [skippedInfo, setSkippedInfo] = useState<string | null>(null);

  // Saved plans
  const [plans, setPlans] = useState<SavedPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | ''>('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');

  // Scale
  const [scaleFactor, setScaleFactor] = useState<number>(1);

  // Prep sheet mode
  const [prepSheet, setPrepSheet] = useState(false);
  const [prepSheetData, setPrepSheetData] = useState<PrepSheetGroup[] | null>(null);
  const [prepSheetLoading, setPrepSheetLoading] = useState(false);

  // Mark purchased
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseItems, setPurchaseItems] = useState<
    { inventoryItemId: number; name: string; unit: string; quantity: number }[]
  >([]);
  const [supplierName, setSupplierName] = useState('');
  const [recordExpense, setRecordExpense] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState<number | ''>('');
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null);

  const selectedItem = menuItems.find((mi) => mi.id === newItemId);

  const toPayloadLines = (ls: PlannerLine[]) =>
    ls.map((l) => ({ menuItemId: l.menuItemId, portionId: l.portionId, quantity: l.quantity }));

  // Load saved plans on mount
  useEffect(() => {
    let cancelled = false;
    inventoryApi
      .getPlans()
      .then((res) => {
        if (!cancelled) setPlans(res.data || []);
      })
      .catch(() => {
        /* non-fatal: planner still works without saved plans */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Any change to the plan lines invalidates the cached per-dish prep sheet
  useEffect(() => {
    setPrepSheetData(null);
  }, [lines]);

  // Build the per-dish prep sheet lazily when the mode is on
  useEffect(() => {
    if (!prepSheet || lines.length === 0 || prepSheetData) return;
    let cancelled = false;
    (async () => {
      setPrepSheetLoading(true);
      try {
        const responses = await Promise.all(
          lines.map((l) =>
            inventoryApi.estimate({
              lines: [{ menuItemId: l.menuItemId, portionId: l.portionId, quantity: l.quantity }],
            })
          )
        );
        if (cancelled) return;
        setPrepSheetData(
          lines.map((line, idx) => ({ line, ingredients: responses[idx].data.ingredients || [] }))
        );
      } catch {
        if (!cancelled) setError('Failed to build the prep sheet');
      } finally {
        if (!cancelled) setPrepSheetLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prepSheet, lines, prepSheetData]);

  const handleAddLine = () => {
    if (!newItemId || newQuantity <= 0 || !selectedItem) return;
    const portionId = newPortionId === '' ? null : (newPortionId as number);
    const portionName = portionId
      ? selectedItem.portions?.find((p) => p.id === portionId)?.portionName || null
      : null;

    setLines((prev) => {
      const existing = prev.find((l) => l.menuItemId === newItemId && l.portionId === portionId);
      if (existing) {
        return prev.map((l) =>
          l === existing ? { ...l, quantity: l.quantity + newQuantity } : l
        );
      }
      return [...prev, { menuItemId: newItemId as number, menuItemName: selectedItem.name, portionId, portionName, quantity: newQuantity }];
    });
    // Results no longer match the plan once it changes
    setResult(null);
    setNewItemId('');
    setNewPortionId('');
    setNewQuantity(0);
  };

  const handleRemoveLine = (line: PlannerLine) => {
    setLines((prev) => prev.filter((l) => l !== line));
    setResult(null);
  };

  const handleEstimate = async () => {
    if (lines.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const res = await inventoryApi.estimate({ lines: toPayloadLines(lines) });
      setResult({
        ingredients: res.data.ingredients || [],
        totalEstimatedCost: res.data.totalEstimatedCost ?? null,
        unmapped: res.data.unmapped || [],
        preps: res.data.preps || [],
      });
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to estimate ingredients');
    } finally {
      setLoading(false);
    }
  };

  /** Convert sales/plan rows into plan lines, skipping items no longer on the menu. */
  const resolveToPlanLines = (
    rows: { menuItemId: number; portionId: number | null; quantity: number }[]
  ): { planLines: PlannerLine[]; skipped: number } => {
    let skipped = 0;
    const planLines: PlannerLine[] = [];
    rows.forEach((row) => {
      const mi = menuItems.find((m) => m.id === row.menuItemId);
      if (!mi) {
        skipped += 1;
        return;
      }
      let portionId: number | null = null;
      let portionName: string | null = null;
      if (row.portionId != null) {
        const p = mi.portions?.find((pp) => pp.id === row.portionId);
        if (p) {
          portionId = p.id;
          portionName = p.portionName;
        }
        // unknown portion → fall back to base recipe (portionId null)
      }
      planLines.push({ menuItemId: mi.id, menuItemName: mi.name, portionId, portionName, quantity: row.quantity });
    });
    return { planLines, skipped };
  };

  const aggregateSales = (allLines: SalesHistoryLine[], divisor: number): SalesHistoryLine[] => {
    const merged = new Map<string, SalesHistoryLine>();
    allLines.forEach((l) => {
      const key = `${l.menuItemId}:${l.portionId ?? 'base'}`;
      const cur = merged.get(key);
      if (cur) cur.quantity += l.quantity;
      else merged.set(key, { ...l, quantity: l.quantity });
    });
    return Array.from(merged.values()).map((l) => ({
      ...l,
      quantity: divisor > 1 ? Math.ceil(l.quantity / divisor) : l.quantity,
    }));
  };

  const applySalesFill = (salesLines: SalesHistoryLine[]) => {
    const { planLines, skipped } = resolveToPlanLines(
      salesLines.map((l) => ({ menuItemId: l.menuItemId, portionId: l.portionId, quantity: l.quantity }))
    );
    setLines(planLines);
    setResult(null);
    setSelectedPlanId('');
    setSkippedInfo(skipped > 0 ? `${skipped} items no longer on the menu were skipped` : null);
  };

  const handleFillFromSales = async (mode: 'yesterday' | 'lastWeek' | 'avg4') => {
    setFillAnchor(null);
    if (lines.length > 0 && !(await confirm({
      title: 'Replace plan',
      description: 'Replace the current plan with sales history?',
      confirmText: 'Replace',
      cancelText: 'Cancel',
    }))) return;
    setFillLoading(true);
    setError(null);
    try {
      const today = new Date();
      if (mode === 'avg4') {
        const dates = [7, 14, 21, 28].map((n) => toIsoDate(addDays(today, -n)));
        const responses = await Promise.all(dates.map((d) => inventoryApi.getSalesHistory(d, d)));
        const combined = responses.flatMap((r) => (r.data.lines || []) as SalesHistoryLine[]);
        applySalesFill(aggregateSales(combined, 4));
      } else {
        const day = toIsoDate(addDays(today, mode === 'yesterday' ? -1 : -7));
        const res = await inventoryApi.getSalesHistory(day, day);
        applySalesFill(aggregateSales((res.data.lines || []) as SalesHistoryLine[], 1));
      }
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to load sales history');
    } finally {
      setFillLoading(false);
    }
  };

  const handleSelectPlan = async (planId: number) => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    if (lines.length > 0 && !(await confirm({
      title: 'Load saved plan',
      description: 'Replace the current plan with the saved one?',
      confirmText: 'Replace',
      cancelText: 'Cancel',
    }))) return;
    const { planLines, skipped } = resolveToPlanLines(
      plan.lines.map((l) => ({ menuItemId: l.menuItemId, portionId: l.portionId, quantity: l.quantity }))
    );
    setLines(planLines);
    setSelectedPlanId(planId);
    setResult(null);
    setSkippedInfo(skipped > 0 ? `${skipped} items no longer on the menu were skipped` : null);
  };

  const openSaveDialog = () => {
    const sel = plans.find((p) => p.id === selectedPlanId);
    setSaveName(sel?.name || '');
    setSaveOpen(true);
  };

  const handleSavePlan = async () => {
    const name = saveName.trim();
    if (!name || lines.length === 0) return;
    try {
      await inventoryApi.savePlan({ name, lines: toPayloadLines(lines) });
      setSaveOpen(false);
      const res = await inventoryApi.getPlans();
      setPlans(res.data || []);
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to save plan');
    }
  };

  const handleDeletePlan = async (e: React.MouseEvent, planId: number) => {
    e.stopPropagation();
    if (!(await confirm({
      title: 'Delete saved plan',
      description: 'Delete this saved plan?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    }))) return;
    try {
      await inventoryApi.deletePlan(planId);
      const res = await inventoryApi.getPlans();
      setPlans(res.data || []);
      if (selectedPlanId === planId) setSelectedPlanId('');
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to delete plan');
    }
  };

  const handleApplyScale = () => {
    if (!scaleFactor || scaleFactor <= 0 || lines.length === 0) return;
    setLines((prev) => prev.map((l) => ({ ...l, quantity: Math.ceil(l.quantity * scaleFactor) })));
    setResult(null);
  };

  const handleCopyList = async () => {
    if (!result) return;
    const listLines = result.ingredients.map((ing) => {
      const base = `${ing.name}: ${formatQuantity(ing.requiredQuantity, ing.unit)}`;
      return ing.shortfall > 0 ? `${base} (buy ${formatQuantity(ing.shortfall, ing.unit)})` : `${base} (in stock)`;
    });
    try {
      await navigator.clipboard.writeText(listLines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const shortfallItems = () => (result ? result.ingredients.filter((i) => i.shortfall > 0) : []);

  const handleShare = () => {
    if (!result) return;
    const dateStr = new Date().toLocaleDateString();
    const items = shortfallItems();
    const text = [
      `Shopping list — ${dateStr}`,
      ...items.map((i) => `• ${i.name}: ${formatQuantity(i.shortfall, i.unit)}`),
    ].join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handlePrint = () => {
    if (!result) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const dateStr = new Date().toLocaleDateString();
    const rows = result.ingredients
      .map(
        (i) =>
          `<tr><td>${i.name}</td><td>${formatQuantity(i.requiredQuantity, i.unit)}</td>` +
          `<td>${formatQuantity(i.currentStock, i.unit)}</td>` +
          `<td>${i.shortfall > 0 ? formatQuantity(i.shortfall, i.unit) : '—'}</td></tr>`
      )
      .join('');
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Shopping list — ${dateStr}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #1e293b; }
            h2 { margin-bottom: 4px; }
            .date { color: #64748b; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
            th { font-size: 13px; text-transform: uppercase; color: #64748b; }
            td:not(:first-child), th:not(:first-child) { text-align: right; }
          </style>
        </head>
        <body>
          <h2>Shopping list</h2>
          <div class="date">${dateStr}</div>
          <table>
            <thead><tr><th>Ingredient</th><th>Required</th><th>In stock</th><th>To buy</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(content);
    printWindow.document.close();
  };

  const openPurchaseDialog = () => {
    if (!result) return;
    setPurchaseItems(
      shortfallItems().map((i) => ({
        inventoryItemId: i.inventoryItemId,
        name: i.name,
        unit: i.unit,
        quantity: Math.round(i.shortfall * 100) / 100,
      }))
    );
    setSupplierName('');
    setRecordExpense(false);
    setExpenseAmount('');
    setPaymentMode('CASH');
    setPurchaseOpen(true);
  };

  const handleSubmitPurchase = async () => {
    const items = purchaseItems
      .filter((i) => i.quantity > 0)
      .map((i) => ({ inventoryItemId: i.inventoryItemId, quantity: i.quantity }));
    if (items.length === 0) return;
    if (recordExpense && (expenseAmount === '' || Number(expenseAmount) <= 0)) return;
    setPurchaseSubmitting(true);
    setError(null);
    try {
      const res = await inventoryApi.recordPurchase({
        items,
        supplierName: supplierName.trim() || undefined,
        createExpense: recordExpense,
        expenseAmount: recordExpense ? Number(expenseAmount) : undefined,
        paymentMode: recordExpense ? paymentMode : undefined,
      });
      const n = res.data.itemsStocked ?? items.length;
      let msg = `Stocked ${n} item${n === 1 ? '' : 's'}`;
      if (res.data.expenseId && res.data.expenseCategoryName) {
        msg += ` and recorded expense in ${res.data.expenseCategoryName}`;
      }
      setPurchaseSuccess(msg);
      setPurchaseOpen(false);
      await handleEstimate();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to record purchase');
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  const lineLabel = (line: PlannerLine) =>
    line.portionName ? `${line.menuItemName} — ${line.portionName}` : line.menuItemName;

  const hasShortfall = !!result && result.ingredients.some((i) => i.shortfall > 0);
  const weekdayLabel = WEEKDAYS[new Date().getDay()];

  const linkBtnSx = { color: theme.primary, textTransform: 'none', fontWeight: 600 } as const;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {purchaseSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPurchaseSuccess(null)} data-testid="planner-purchase-success">
          {purchaseSuccess}
        </Alert>
      )}
      {menuItems.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No menu items found. Create your menu first (sidebar → Menu) to plan production.
        </Alert>
      )}

      {/* Build the plan */}
      <Card sx={{
        backgroundColor: theme.components?.card?.background,
        border: `1px solid ${theme.components?.card?.border}`,
        borderRadius: 2,
        mb: 3,
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text }}>
              Production Plan
            </Typography>
            <Button
              size="small"
              onClick={(e) => setFillAnchor(e.currentTarget)}
              disabled={fillLoading || menuItems.length === 0}
              startIcon={fillLoading ? <CircularProgress size={14} /> : undefined}
              data-testid="planner-fill-sales"
              sx={linkBtnSx}
            >
              Fill from sales
            </Button>
            <Menu anchorEl={fillAnchor} open={Boolean(fillAnchor)} onClose={() => setFillAnchor(null)}>
              <MuiMenuItem onClick={() => handleFillFromSales('yesterday')} data-testid="planner-fill-yesterday">
                Yesterday
              </MuiMenuItem>
              <MuiMenuItem onClick={() => handleFillFromSales('lastWeek')} data-testid="planner-fill-lastweek">
                Same day last week
              </MuiMenuItem>
              <MuiMenuItem onClick={() => handleFillFromSales('avg4')} data-testid="planner-fill-avg4">
                Average of last 4 {weekdayLabel}s
              </MuiMenuItem>
            </Menu>
          </Box>
          <Typography variant="body2" sx={{ color: theme.gray?.[600], mb: 2 }}>
            Add the dishes you plan to make and estimate the ingredients needed
          </Typography>

          {skippedInfo && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSkippedInfo(null)} data-testid="planner-skipped-alert">
              {skippedInfo}
            </Alert>
          )}

          {/* Saved plans + scale row */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Saved plans</InputLabel>
                <Select
                  value={selectedPlanId}
                  label="Saved plans"
                  disabled={plans.length === 0}
                  onChange={(e) => handleSelectPlan(Number(e.target.value))}
                  renderValue={(val) => plans.find((p) => p.id === val)?.name || ''}
                  data-testid="planner-plan-select"
                >
                  {plans.map((plan) => (
                    <MuiMenuItem key={plan.id} value={plan.id} sx={{ justifyContent: 'space-between', gap: 1 }}>
                      <span>{plan.name}</span>
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label={`Delete ${plan.name}`}
                        onClick={(e) => handleDeletePlan(e, plan.id)}
                        data-testid={`planner-plan-delete-${plan.id}`}
                      >
                        <TrashIcon style={{ width: 16, height: 16, color: theme.danger }} />
                      </IconButton>
                    </MuiMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs="auto">
              <Button
                size="small"
                onClick={openSaveDialog}
                disabled={lines.length === 0}
                data-testid="planner-save-plan"
                sx={linkBtnSx}
              >
                Save plan
              </Button>
            </Grid>
            {lines.length > 0 && (
              <Grid item xs="auto">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    label="× Scale"
                    type="number"
                    size="small"
                    value={scaleFactor}
                    onChange={(e) => setScaleFactor(parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0.1, step: 0.5, 'data-testid': 'planner-scale-input' }}
                    sx={{ width: 96 }}
                  />
                  <Button size="small" onClick={handleApplyScale} data-testid="planner-scale-apply" sx={linkBtnSx}>
                    Apply
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Menu Item</InputLabel>
                <Select
                  value={newItemId}
                  onChange={(e) => { setNewItemId(Number(e.target.value)); setNewPortionId(''); }}
                  label="Menu Item"
                  data-testid="planner-item-select"
                >
                  {menuItems.map((mi) => (
                    <MuiMenuItem key={mi.id} value={mi.id}>
                      {mi.name} {mi.categoryName ? `(${mi.categoryName})` : ''}
                    </MuiMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {selectedItem?.portions && selectedItem.portions.length > 0 && (
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Portion</InputLabel>
                  <Select
                    value={newPortionId}
                    onChange={(e) => setNewPortionId(e.target.value === '' ? '' : Number(e.target.value))}
                    label="Portion"
                    data-testid="planner-portion-select"
                  >
                    <MuiMenuItem value="">Standard</MuiMenuItem>
                    {selectedItem.portions.map((p) => (
                      <MuiMenuItem key={p.id} value={p.id}>{p.portionName}</MuiMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6} sm={2}>
              <TextField
                label="Plates"
                type="number"
                size="small"
                fullWidth
                value={newQuantity || ''}
                onChange={(e) => setNewQuantity(parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 1, step: 1, 'data-testid': 'planner-qty-input' }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                startIcon={<PlusIcon className="h-4 w-4" />}
                onClick={handleAddLine}
                disabled={!newItemId || newQuantity <= 0}
                data-testid="planner-add-line"
                sx={{
                  backgroundColor: `${theme.button?.secondary?.bg || theme.background} !important`,
                  color: `${theme.primary} !important`,
                  border: `1px solid ${theme.primary} !important`,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none !important',
                  '&:disabled': { opacity: 0.5 },
                }}
              >
                Add
              </Button>
            </Grid>
          </Grid>

          {/* Plan lines */}
          {lines.length > 0 && (
            <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {lines.map((line) => (
                <Box
                  key={`${line.menuItemId}-${line.portionId ?? 'base'}`}
                  data-testid="planner-line"
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.5, py: 1, borderRadius: 1.5,
                    backgroundColor: theme.background,
                    border: `1px solid ${theme.components?.card?.border}`,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>
                    {lineLabel(line)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>
                      × {line.quantity}
                    </Typography>
                    <AppIconButton
                      variant="danger"
                      tooltip="Remove"
                      size="small"
                      icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                      onClick={() => handleRemoveLine(line)}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleEstimate}
              disabled={lines.length === 0 || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <CalculatorIcon className="h-4 w-4" />}
              data-testid="planner-estimate-btn"
              sx={{
                backgroundColor: `${theme.accent}22 !important`,
                color: `${theme.accent} !important`,
                border: `1px solid ${theme.accent} !important`,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none !important',
                '&:hover': { backgroundColor: `${theme.accent}44 !important`, boxShadow: 'none !important' },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              Estimate Ingredients
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card sx={{
          backgroundColor: theme.components?.card?.background,
          border: `1px solid ${theme.components?.card?.border}`,
          borderRadius: 2,
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text }}>
                Ingredients Needed
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={prepSheet}
                      onChange={(e) => setPrepSheet(e.target.checked)}
                      data-testid="planner-prepsheet-toggle"
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: theme.text }}>Prep sheet</Typography>}
                />
                {result.ingredients.length > 0 && (
                  <>
                    <Button
                      size="small"
                      startIcon={<ClipboardDocumentIcon className="h-4 w-4" />}
                      onClick={handleCopyList}
                      data-testid="planner-copy-btn"
                      sx={linkBtnSx}
                    >
                      {copied ? 'Copied!' : 'Copy purchase list'}
                    </Button>
                    <Button size="small" onClick={handleShare} data-testid="planner-share-btn" sx={linkBtnSx}>
                      Share
                    </Button>
                    <Button size="small" onClick={handlePrint} data-testid="planner-print-btn" sx={linkBtnSx}>
                      Print
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            {hasShortfall && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={openPurchaseDialog}
                  data-testid="planner-purchase-btn"
                  sx={{
                    backgroundColor: `${theme.primary}22 !important`,
                    color: `${theme.primary} !important`,
                    border: `1px solid ${theme.primary} !important`,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 'none !important',
                    '&:hover': { backgroundColor: `${theme.primary}44 !important`, boxShadow: 'none !important' },
                  }}
                >
                  Mark purchased
                </Button>
              </Box>
            )}

            {result.unmapped.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="planner-unmapped-alert">
                No recipe found for:{' '}
                {result.unmapped
                  .map((u) => (u.portionName ? `${u.menuItemName} (${u.portionName})` : u.menuItemName))
                  .join(', ')}
                . Add recipes in the Recipe Mappings tab to include them.
              </Alert>
            )}

            {prepSheet ? (
              /* Per-dish prep sheet */
              prepSheetLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : prepSheetData && prepSheetData.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="planner-prepsheet">
                  {prepSheetData.map((group) => (
                    <Box
                      key={`${group.line.menuItemId}-${group.line.portionId ?? 'base'}`}
                      data-testid="planner-prepsheet-group"
                      sx={{
                        p: 1.5, borderRadius: 1.5,
                        border: `1px solid ${theme.components?.card?.border}`,
                        backgroundColor: theme.background,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text, mb: 1 }}>
                        {lineLabel(group.line)} × {group.line.quantity}
                      </Typography>
                      {group.ingredients.length > 0 ? (
                        <IngredientList ings={group.ingredients} theme={theme} />
                      ) : (
                        <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                          No recipe for this dish yet.
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: theme.gray?.[600], py: 3, textAlign: 'center' }}>
                  Nothing to show for the prep sheet.
                </Typography>
              )
            ) : result.ingredients.length === 0 ? (
              <Typography variant="body2" sx={{ color: theme.gray?.[600], py: 3, textAlign: 'center' }}>
                No ingredients to show — none of the planned items have recipes yet.
              </Typography>
            ) : isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {result.ingredients.map((ing) => (
                  <Box
                    key={ing.inventoryItemId}
                    data-testid="planner-ingredient-row"
                    sx={{
                      p: 1.5, borderRadius: 1.5,
                      border: `1px solid ${ing.shortfall > 0 ? theme.warning : theme.components?.card?.border}`,
                      backgroundColor: ing.shortfall > 0 ? `${theme.warning}10` : theme.background,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>{ing.name}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>
                        {formatQuantity(ing.requiredQuantity, ing.unit)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                        In stock: {formatQuantity(ing.currentStock, ing.unit)}
                      </Typography>
                      {ing.shortfall > 0 && (
                        <Typography variant="caption" sx={{ color: theme.warning, fontWeight: 700 }}>
                          Buy {formatQuantity(ing.shortfall, ing.unit)}
                        </Typography>
                      )}
                      {ing.estimatedCost != null && (
                        <Typography variant="caption" sx={{ color: theme.gray?.[600], ml: 'auto' }}>
                          {formatCurrency(ing.estimatedCost)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }}>Ingredient</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Required</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">In Stock</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">To Buy</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Est. Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.ingredients.map((ing) => (
                      <TableRow
                        key={ing.inventoryItemId}
                        data-testid="planner-ingredient-row"
                        sx={ing.shortfall > 0 ? { backgroundColor: `${theme.warning}10` } : {}}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>{ing.name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                            {formatQuantity(ing.requiredQuantity, ing.unit)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                            {formatQuantity(ing.currentStock, ing.unit)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {ing.shortfall > 0 ? (
                            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.warning }}>
                              {formatQuantity(ing.shortfall, ing.unit)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: theme.success }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: theme.text }}>
                            {ing.estimatedCost != null ? formatCurrency(ing.estimatedCost) : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {result.totalEstimatedCost != null && (
                      <TableRow data-testid="planner-total-row">
                        <TableCell colSpan={4}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>
                            Total estimated cost
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text }}>
                            {formatCurrency(result.totalEstimatedCost)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Prepared (kitchen-made) ingredients that are short */}
            {result.preps.length > 0 && (
              <Box sx={{ mt: 3 }} data-testid="planner-prep-section">
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.text, mb: 1.5 }}>
                  To prepare in kitchen
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {result.preps.map((prep) => (
                    <Box
                      key={prep.inventoryItemId}
                      data-testid="planner-prep-card"
                      sx={{
                        p: 1.5, borderRadius: 1.5,
                        border: `1px solid ${theme.components?.card?.border}`,
                        backgroundColor: theme.background,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: theme.text, mb: 1 }}>
                        {prep.name}: make {formatQuantity(prep.quantityToMake, prep.unit)}
                      </Typography>
                      {prep.ingredients.length > 0 && (
                        <IngredientList ings={prep.ingredients} theme={theme} />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save plan dialog */}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ color: theme.text }}>Save plan</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Plan name"
            size="small"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            inputProps={{ 'data-testid': 'planner-save-name' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)} sx={{ color: theme.gray?.[600], textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePlan}
            disabled={!saveName.trim() || lines.length === 0}
            data-testid="planner-save-submit"
            sx={linkBtnSx}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark purchased dialog */}
      <Dialog open={purchaseOpen} onClose={() => setPurchaseOpen(false)} fullWidth maxWidth="sm" data-testid="planner-purchase-dialog">
        <DialogTitle sx={{ color: theme.text }}>Mark purchased</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {purchaseItems.map((item, idx) => (
              <Box key={item.inventoryItemId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" sx={{ flex: 1, color: theme.text }}>{item.name}</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={item.quantity}
                  onChange={(e) => {
                    const q = parseFloat(e.target.value);
                    setPurchaseItems((prev) =>
                      prev.map((p, i) => (i === idx ? { ...p, quantity: Number.isNaN(q) ? 0 : q } : p))
                    );
                  }}
                  inputProps={{ min: 0, step: 0.1, 'data-testid': `planner-purchase-qty-${item.inventoryItemId}` }}
                  sx={{ width: 110 }}
                />
                <Typography variant="caption" sx={{ color: theme.gray?.[600], width: 40 }}>
                  {unitLabel(item.unit)}
                </Typography>
              </Box>
            ))}

            <TextField
              label="Supplier (optional)"
              size="small"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              inputProps={{ 'data-testid': 'planner-purchase-supplier' }}
              sx={{ mt: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={recordExpense}
                  onChange={(e) => setRecordExpense(e.target.checked)}
                  data-testid="planner-purchase-expense-toggle"
                />
              }
              label={<Typography variant="body2" sx={{ color: theme.text }}>Record as expense</Typography>}
            />

            {recordExpense && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Amount paid"
                  type="number"
                  size="small"
                  required
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  inputProps={{ min: 0, step: 0.01, 'data-testid': 'planner-purchase-amount' }}
                  sx={{ flex: 1, minWidth: 140 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Payment mode</InputLabel>
                  <Select
                    value={paymentMode}
                    label="Payment mode"
                    onChange={(e) => setPaymentMode(e.target.value)}
                    data-testid="planner-purchase-mode"
                  >
                    {PAYMENT_MODES.map((m) => (
                      <MuiMenuItem key={m} value={m}>{m.replace('_', ' ')}</MuiMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseOpen(false)} sx={{ color: theme.gray?.[600], textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitPurchase}
            disabled={
              purchaseSubmitting ||
              purchaseItems.every((i) => i.quantity <= 0) ||
              (recordExpense && (expenseAmount === '' || Number(expenseAmount) <= 0))
            }
            startIcon={purchaseSubmitting ? <CircularProgress size={14} /> : undefined}
            data-testid="planner-purchase-submit"
            sx={linkBtnSx}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlannerTab;
